import {useIntl, defineMessages} from 'react-intl'
import {formatPhoneNumber} from '@salesforce/retail-react-app/app/utils/phone-utils'
import {
    stateOptions,
    provinceOptions
} from '@salesforce/retail-react-app/app/components/forms/state-province-options'
import {SHIPPING_COUNTRY_CODES} from '../../static/constants'

const messages = defineMessages({
    required: {defaultMessage: 'Required', id: 'use_address_fields.error.required'},
    c_addressName: {
        defaultMessage: 'Name this Address',
        id: 'use_address_fields.label.c_addressName'
    },
    c_addressName_for_account_address: {
        defaultMessage: 'Address Nickname',
        id: 'use_address_fields.label.c_addressName_for_account_address'
    },
    firstName: {defaultMessage: 'First Name', id: 'use_address_fields.label.first_name'},
    lastName: {defaultMessage: 'Last Name', id: 'use_address_fields.label.last_name'},
    phone: {defaultMessage: 'Phone', id: 'use_address_fields.label.phone'},
    country: {defaultMessage: 'Country', id: 'use_address_fields.label.country'},
    address: {defaultMessage: 'Street Address', id: 'use_address_fields.label.street_address'},
    city: {defaultMessage: 'City', id: 'use_address_fields.label.city'},
    state: {defaultMessage: 'State', id: 'use_address_fields.label.state'},
    province: {defaultMessage: 'Province', id: 'use_address_fields.label.province'},
    zipCode: {defaultMessage: 'Zip Code', id: 'use_address_fields.label.zipCode'},
    postalCode: {defaultMessage: 'Postal Code', id: 'use_address_fields.label.postal_code'},
    stateCodeInvalid: {
        defaultMessage: 'Please enter 2-letter state/province.',
        id: 'use_address_fields.error.state_code_invalid'
    },
    preferred: {
        defaultMessage: `Save address to My Cavender’s Account`,
        id: 'use_address_fields.label.save_as_preferred'
    },
    preferred_account_page: {
        defaultMessage: 'Set as default address',
        id: 'use_address_fields.label.preferred_account_page'
    }
})

export default function useAddressFields(
    {
        form: {
            watch,
            control,
            formState: {errors}
        },
        prefix = ''
    },
    isAccountAddress = false
) {
    const {formatMessage} = useIntl()

    const countryCode = watch('countryCode')

    const fields = {
        c_addressName: {
            name: `${prefix}c_addressName`,
            label: formatMessage(
                isAccountAddress
                    ? messages.c_addressName_for_account_address
                    : messages.c_addressName
            ),
            defaultValue: '',
            type: 'text',
            autoComplete: 'given-name',
            rules: {
                pattern: {
                    value: /^[A-Za-z0-9\s.,-]+$/,
                    message: formatMessage({
                        defaultMessage: 'Special characters are not allowed.',
                        id: 'use_address_fields.error.invalid_name'
                    })
                }
            },
            error: errors[`${prefix}c_addressName`],
            control
        },
        firstName: {
            name: `${prefix}firstName`,
            label: formatMessage(messages.firstName),
            defaultValue: '',
            type: 'text',
            autoComplete: 'given-name',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your first name.',
                    id: 'use_address_fields.error.please_enter_first_name'
                }),
                pattern: {
                    value: /^[A-Za-z0-9\s.,-]+$/,
                    message: formatMessage({
                        defaultMessage: 'Special characters are not allowed.',
                        id: 'use_address_fields.error.invalid_name'
                    })
                }
            },
            error: errors[`${prefix}firstName`],
            control
        },
        lastName: {
            name: `${prefix}lastName`,
            label: formatMessage(messages.lastName),
            defaultValue: '',
            type: 'text',
            autoComplete: 'family-name',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your last name.',
                    id: 'use_address_fields.error.please_enter_last_name'
                }),
                pattern: {
                    value: /^[A-Za-z0-9\s.,-]+$/,
                    message: formatMessage({
                        defaultMessage: 'Special characters are not allowed.',
                        id: 'use_address_fields.error.invalid_name'
                    })
                }
            },
            error: errors[`${prefix}lastName`],
            control
        },
        phone: {
            name: `${prefix}phone`,
            label: formatMessage(messages.phone),
            defaultValue: '',
            type: 'phone',
            autoComplete: 'tel',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your phone number.',
                    id: 'use_address_fields.error.please_enter_phone_number'
                }),
                validate: (value) => {
                    const digitsOnly = value.replace(/\D/g, '')
                    if (!/^[2-9]\d{2}[2-9](?!11)\d{6}$/.test(digitsOnly)) {
                        return formatMessage({
                            defaultMessage: 'Please enter a valid 10-digit phone number.',
                            id: 'use_registration_fields.error.invalid_phone'
                        })
                    }
                    return true
                }
            },
            error: errors[`${prefix}phone`],
            inputProps: ({onChange}) => ({
                inputMode: 'numeric',
                onChange(evt) {
                    onChange(formatPhoneNumber(evt.target.value))
                }
            }),
            control
        },
        countryCode: {
            name: `${prefix}countryCode`,
            label: formatMessage(messages.country),
            defaultValue: 'US',
            type: 'select',
            options: SHIPPING_COUNTRY_CODES,
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please select your country.',
                    id: 'use_address_fields.error.please_select_your_country'
                })
            },
            error: errors[`${prefix}countryCode`],
            control
        },
        address1: {
            name: `${prefix}address1`,
            label: formatMessage(messages.address),
            defaultValue: '',
            type: 'text',
            autoComplete: 'address-line1',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your address.',
                    id: 'use_address_fields.error.please_select_your_address'
                }),
                pattern: {
                    value: /^[A-Za-z0-9\s.,-]+$/,
                    message: formatMessage({
                        defaultMessage: 'Special characters are not allowed.',
                        id: 'use_address_fields.error.invalid_name'
                    })
                }
            },
            error: errors[`${prefix}address1`],
            control
        },
        city: {
            name: `${prefix}city`,
            label: formatMessage(messages.city),
            defaultValue: '',
            type: 'text',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your city.',
                    id: 'use_address_fields.error.please_select_your_city'
                }),
                pattern: {
                    value: /^[A-Za-z0-9\s.,-]+$/,
                    message: formatMessage({
                        defaultMessage: 'Special characters are not allowed.',
                        id: 'use_address_fields.error.invalid_name'
                    })
                }
            },
            error: errors[`${prefix}city`],
            control
        },
        stateCode: {
            name: `${prefix}stateCode`,
            label: formatMessage(countryCode === 'CA' ? messages.province : messages.state),
            defaultValue: '',
            type: 'select',
            options: [
                {value: '', label: ''},
                ...(countryCode === 'CA' ? provinceOptions : stateOptions)
            ],
            rules: {
                required:
                    countryCode === 'CA'
                        ? 'Please select your province.' // FYI we won't translate this
                        : formatMessage({
                              defaultMessage: 'Please select your state.',
                              id: 'use_address_fields.error.please_select_your_state_or_province',
                              description: 'Error message for a blank state (US-specific checkout)'
                          })
            },
            error: errors[`${prefix}stateCode`],
            control
        },
        postalCode: {
            name: `${prefix}postalCode`,
            label: formatMessage(countryCode === 'CA' ? messages.postalCode : messages.zipCode),
            defaultValue: '',
            type: 'text',
            autoComplete: 'postal-code',
            rules: {
                required:
                    countryCode === 'CA'
                        ? 'Please enter your postal code.' // FYI we won't translate this
                        : formatMessage({
                              defaultMessage: 'Please enter your zip code.',
                              id: 'use_address_fields.error.please_enter_your_postal_or_zip',
                              description:
                                  'Error message for a blank zip code (US-specific checkout)'
                          }),
                pattern:
                    countryCode !== 'CA'
                        ? {
                              value: /^\d{5}$/,
                              message: formatMessage({
                                  defaultMessage: 'ZIP code must be exactly 5 digits.',
                                  id: 'use_address_fields.error.invalid_us_zip'
                              })
                          }
                        : undefined
            },
            error: errors[`${prefix}postalCode`],
            control
        },
        preferred: {
            name: `${prefix}preferred`,
            label: formatMessage(
                isAccountAddress ? messages.preferred_account_page : messages.preferred
            ),
            defaultValue: false,
            type: 'checkbox',
            autoComplete: 'honorific-prefix',
            rules: {},
            textTransfor: 'lowercase',
            control
        }
    }

    return fields
}
